import React, { useState, useRef } from 'react';
import { doc, updateDoc, arrayRemove, arrayUnion, increment } from 'firebase/firestore';
import { db } from '../../config/firebase';

export default function ListingCard({ listing, vendor, navigateTo, user, isFavorite }) {
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const touchStartX = useRef(0);
    const touchEndX = useRef(0);

    const displayImage = listing.imageUrls && listing.imageUrls.length > 0
        ? listing.imageUrls[currentImageIndex]
        : 'https://placehold.co/600x400/f2a2e8/ffffff?text=Party+Item';

    const toggleFavorite = async (e) => {
        e.stopPropagation();
        e.preventDefault(); 
        if (!user || user.isAnonymous) {
            navigateTo('login');
            return;
        }
        const userRef = doc(db, "users", user.uid);
        const listingRef = doc(db, "listings", listing.id);
        if (isFavorite) {
            await updateDoc(userRef, { favorites: arrayRemove(listing.id) });
            await updateDoc(listingRef, { wishlistCount: increment(-1) });
        } else {
            await updateDoc(userRef, { favorites: arrayUnion(listing.id) });
            await updateDoc(listingRef, { wishlistCount: increment(1) });
        }
    };

    const nextImage = (e) => {
        e.stopPropagation();
        e.preventDefault();
        setCurrentImageIndex(prev => (prev + 1) % listing.imageUrls.length);
    };

    const prevImage = (e) => {
        e.stopPropagation();
        e.preventDefault();
        setCurrentImageIndex(prev => (prev - 1 + listing.imageUrls.length) % listing.imageUrls.length);
    };

    const handleTouchStart = (e) => {
        touchStartX.current = e.targetTouches[0].clientX;
    };

    const handleTouchMove = (e) => {
        touchEndX.current = e.targetTouches[0].clientX;
    };

    const handleTouchEnd = () => {
        if (touchStartX.current - touchEndX.current > 50) {
            setCurrentImageIndex(prev => (prev + 1) % listing.imageUrls.length);
        }

        if (touchStartX.current - touchEndX.current < -50) {
            setCurrentImageIndex(prev => (prev - 1 + listing.imageUrls.length) % listing.imageUrls.length);
        }
    };

    return (
        <a 
            href={`#listingDetail/${listing.id}`}
            onClick={(e) => { e.preventDefault(); navigateTo('listingDetail', listing); }}
            className="block bg-white rounded-2xl shadow-sm hover:shadow-xl overflow-hidden transform hover:-translate-y-1 transition-all duration-300 group border border-gray-100 cursor-pointer text-left"
        >
            <div
                className="relative"
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
            >
                <img
                    src={displayImage}
                    loading="lazy" 
                    onError={(e) => { e.target.onerror = null; e.target.src='https://placehold.co/600x400/f2a2e8/ffffff?text=Image+Missing'; }}
                    alt={listing.title}
                    className="w-full h-48 object-cover bg-gray-50"
                />

                {listing.imageUrls && listing.imageUrls.length > 1 && (
                    <>
                        <button onClick={prevImage} className="absolute top-1/2 left-2 -translate-y-1/2 bg-black/50 backdrop-blur-sm text-white p-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity md:block hidden hover:bg-black/70">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" /></svg>
                        </button>
                        <button onClick={nextImage} className="absolute top-1/2 right-2 -translate-y-1/2 bg-black/50 backdrop-blur-sm text-white p-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity md:block hidden hover:bg-black/70">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" /></svg>
                        </button>
                    </>
                )}

                {user && !user.isVendor && (
                     <button onClick={toggleFavorite} className="absolute top-2 right-2 bg-white/90 backdrop-blur-sm rounded-full p-2 text-pink-500 hover:bg-white transition-opacity opacity-0 group-hover:opacity-100 shadow-sm">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill={isFavorite ? 'currentColor' : 'none'} viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4.318 6.318a4.5 4.5 0 016.364 0L12 7.636l1.318-1.318a4.5 4.5 0 116.364 5.656L12 20.364l-7.682-7.682a4.5 4.5 0 010-6.364z" />
                        </svg>
                    </button>
                )}
            </div>
            <div className="p-4">
                <div className="flex justify-between items-start">
                    <span
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            navigateTo('listings', { category: listing.category });
                        }}
                        className={`text-[10px] ${listing.isService ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-600'} px-2 py-1 rounded-md font-bold uppercase tracking-wider cursor-pointer hover:opacity-80 transition-opacity`}
                    >
                        {listing.isService && listing.serviceType ? listing.serviceType : (listing.category || 'misc')}
                    </span>
                    {listing.capacity && (
                        <span className="text-[10px] bg-blue-50 text-blue-600 px-2 py-1 rounded-md font-bold uppercase tracking-wider flex items-center gap-1">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                                <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z" />
                            </svg>
                            {listing.capacity} ppl
                        </span>
                    )}
                </div>
                <h3 className="font-bold text-lg mt-2 truncate text-gray-900">{listing.title}</h3>
                
                {/* Updated Price Display */}
                <p className="text-pink-600 font-bold mt-1">${listing.price || '0.00'} <span className="text-xs text-gray-500 font-medium">/ day</span></p>

                <div className="flex items-center mt-1">
                    <div className="flex text-yellow-400 text-xs">
                        {[...Array(5)].map((_, i) => (
                            <span key={i}>{i < Math.round(listing.averageRating || 0) ? '★' : '☆'}</span>
                        ))}
                    </div>
                    <span className="text-gray-400 text-xs ml-1">({listing.reviewCount || 0})</span>
                </div>

                {vendor && (
                    <div className="flex items-center mt-2">
                        <img
                            src={vendor?.profilePicUrl || 'https://placehold.co/32x32/e2e8f0/64748b?text=V'}
                            onError={(e) => { e.target.onerror = null; e.target.src='https://placehold.co/32x32/e2e8f0/64748b?text=V'; }}
                            alt={vendor?.vendorName}
                            className="w-5 h-5 rounded-full mr-2 object-cover bg-gray-200 border border-white shadow-sm"
                        />
                        <p className="text-gray-500 text-xs font-medium truncate">{vendor?.vendorName || listing.vendorEmail}</p>
                    </div>
                )}
            </div>
        </a>
    );
}